import { apiGet, apiPost, apiPut, apiDelete, apiUpload } from './client';

// ===== Types =====

export interface ExtraCost {
  id: number;
  userId: number;
  user: { id: number; email: string; firstName: string | null; lastName: string | null } | null;
  description: string;
  amount: number;
  costDate: string;
  typeId: number | null;
  typeName: string | null;
  appliedAmount: number;
  remainingAmount: number;
  status: 'new' | 'pending' | 'partial' | 'applied';
  recurring: boolean;
  frequencyMonths: number | null;
  state: string;
  hasFile: boolean;
  fileName: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ExtraCostApplication {
  id: number;
  month: number;
  year: number;
  appliedAmount: number;
  invoiceState: string | null;
}

export interface ExtraCostDetail extends ExtraCost {
  fileContentType: string | null;
  fileSize: number | null;
  applications: ExtraCostApplication[];
}

export interface ExtraCostOverviewItem {
  userId: number;
  userName: string;
  totalCosts: number;
  totalApplied: number;
  totalRemaining: number;
  costCount: number;
}

export interface CreateExtraCostInput {
  userId: number;
  description: string;
  amount: number;
  costDate: string;
  typeId?: number | null;
  recurring?: boolean;
  frequencyMonths?: number | null;
}

export interface UpdateExtraCostInput {
  description?: string;
  amount?: number;
  costDate?: string;
  typeId?: number | null;
  recurring?: boolean;
  frequencyMonths?: number | null;
}

export interface ExtraCostFilters {
  userId?: number;
  status?: string;
  typeId?: number;
  from?: string;
  to?: string;
}

// ===== API Functions =====

export async function getExtraCosts(filters?: ExtraCostFilters): Promise<ExtraCost[]> {
  const params = new URLSearchParams();
  if (filters?.userId) params.set('userId', String(filters.userId));
  if (filters?.status) params.set('status', filters.status);
  if (filters?.typeId) params.set('typeId', String(filters.typeId));
  if (filters?.from) params.set('from', filters.from);
  if (filters?.to) params.set('to', filters.to);
  const query = params.toString();
  const res = await apiGet<{ data: ExtraCost[] }>(`/admin/employee-extra-costs${query ? `?${query}` : ''}`);
  return res.data;
}

export async function getExtraCostById(id: number): Promise<ExtraCostDetail> {
  const res = await apiGet<{ data: ExtraCostDetail }>(`/admin/employee-extra-costs/${id}`);
  return res.data;
}

export async function createExtraCost(input: CreateExtraCostInput): Promise<ExtraCost> {
  const res = await apiPost<{ data: ExtraCost }>('/admin/employee-extra-costs', input);
  return res.data;
}

export async function updateExtraCost(id: number, input: UpdateExtraCostInput): Promise<ExtraCost> {
  const res = await apiPut<{ data: ExtraCost }>(`/admin/employee-extra-costs/${id}`, input);
  return res.data;
}

export async function deleteExtraCost(id: number): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/admin/employee-extra-costs/${id}`);
}

export async function uploadExtraCostFile(
  id: number,
  file: File
): Promise<{ hasFile: boolean; fileName: string | null; fileContentType: string | null; fileSize: number | null }> {
  const res = await apiUpload<{
    data: { hasFile: boolean; fileName: string | null; fileContentType: string | null; fileSize: number | null };
  }>(`/admin/employee-extra-costs/${id}/upload`, file);
  return res.data;
}

export async function getExtraCostDownloadUrl(id: number): Promise<string> {
  const res = await apiGet<{ data: { url: string } }>(`/admin/employee-extra-costs/${id}/download`);
  return res.data.url;
}

export async function getExtraCostOverview(): Promise<ExtraCostOverviewItem[]> {
  const res = await apiGet<{ data: ExtraCostOverviewItem[] }>('/admin/employee-extra-costs/overview');
  return res.data;
}

export async function approveExtraCosts(ids: number[]): Promise<{ approved: number }> {
  const res = await apiPost<{ data: { approved: number } }>('/admin/employee-extra-costs/approve', { ids });
  return res.data;
}

export async function getMyExtraCosts(): Promise<ExtraCost[]> {
  const res = await apiGet<{ data: ExtraCost[] }>('/my-extra-costs');
  return res.data;
}
